// powerFlow.js

const FLOW_DURATION_MAX_SECS = 8;  // Langsamste Animation (wenig Fluss)
const FLOW_DURATION_MIN_SECS = 1;  // Schnellste Animation (viel Fluss)
const FLOW_POWER_MAX_KW = 20;      // Schwellenwert in kW, ab dem maximale Geschwindigkeit erreicht wird

let _svgContainer, _svg, _flowPv, _flowConsumer, _flowGrid, _pvToConsumerTrack, _pvToGridTrack,
  _gridToConsumerTrack, _pvToConsumerDot, _pvToGridDot, _gridToConsumerDot, _pvPower, _gridPower, _consumerPower,
  _pvToConsumerPower, _pvToGridPower, _gridToConsumerPower, _pvToConsumerFlow, _pvToConsumerDirty, _pvToGridFlow,
  _pvToGridDirty, _gridToConsumerFlow, _gridToConsumerDirty, _circleConsumerPv, _circleConsumerGrid;

const _resizeObserver = new ResizeObserver((entries) => {
  if (entries.length === 0) {
    return;
  }

  const entry = entries[0];
  if (entry.borderBoxSize?.length > 0) {
    const w = entry.borderBoxSize[0].inlineSize;
    const h = entry.borderBoxSize[0].blockSize;

    const top = 60;
    const leftX = 50;
    const bottom = h - 60;
    const right = w - 50;
    const gap = 10;

    const pvToConsumerX0 = w / 2 - gap / 2;
    const pvToConsumerY0 = top + 30;
    const pvToConsumerX1 = leftX + 30;
    const pvToConsumerY1 = bottom - gap / 2;

    const pvToGridX0 = w / 2 + gap / 2;
    const pvToGridY0 = top + 30;
    const pvToGridX1 = right - 30;
    const pvToGridY1 = bottom - gap / 2;

    const gridToConsumerX0 = right - 30;
    const gridToConsumerY0 = bottom + gap / 2;
    const gridToConsumerX1 = leftX + 30;
    const gridToConsumerY1 = bottom + gap / 2;

    const pvToConsumerPath = 'M' + pvToConsumerX0 + ' ' + pvToConsumerY0 + ' C ' + pvToConsumerX0 + ' ' + pvToConsumerY1 + ', ' + pvToConsumerX0 + ' ' + pvToConsumerY1 + ', ' + pvToConsumerX1 + ' ' + pvToConsumerY1;
    const pvToGridTrackPath = 'M' + pvToGridX0 + ' ' + pvToGridY0 + ' C ' + pvToGridX0 + ' ' + pvToGridY1 + ', ' + pvToGridX0 + ' ' + pvToGridY1 + ', ' + pvToGridX1 + ' ' + pvToGridY1;
    const gridToConsumerPath = 'M' + gridToConsumerX0 + ' ' + gridToConsumerY0 + ' L' + gridToConsumerX1 + ' ' + gridToConsumerY1;

    _svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);

    _flowPv.setAttribute('transform', 'translate(' + (w / 2) + ', ' + top + ')');
    _flowConsumer.setAttribute('transform', 'translate(' + leftX + ', ' + bottom + ')');
    _flowGrid.setAttribute('transform', 'translate(' + right + ', ' + bottom + ')');

    _pvToConsumerPower.setAttribute('x', (pvToConsumerX0 - 15).toString());
    _pvToConsumerPower.setAttribute('y', (pvToConsumerY0 + (pvToConsumerY1 - pvToConsumerY0) / 2).toString());
    _pvToGridPower.setAttribute('x', (pvToGridX0 + 15).toString());
    _pvToGridPower.setAttribute('y', (pvToGridY0 + (pvToGridY1 - pvToGridY0) / 2).toString());
    _gridToConsumerPower.setAttribute('x', (w / 2).toString());
    _gridToConsumerPower.setAttribute('y', (gridToConsumerY0 + 5).toString());

    _pvToConsumerTrack.setAttribute('d', pvToConsumerPath);
    _pvToConsumerDot.style.offsetPath = 'path("' + pvToConsumerPath + '")';

    _pvToGridTrack.setAttribute('d', pvToGridTrackPath);
    _pvToGridDot.style.offsetPath = 'path("' + pvToGridTrackPath + '")';

    _gridToConsumerTrack.setAttribute('d', gridToConsumerPath);
    _gridToConsumerDot.style.offsetPath = 'path("' + gridToConsumerPath + '")';

    _pvToConsumerDirty = true;
    _pvToGridDirty = true;
    _gridToConsumerDirty = true;
  }
});

const _getTrackMaxLength = () => {
  return Math.max(_pvToConsumerTrack.getTotalLength(), _pvToGridTrack.getTotalLength(), _gridToConsumerTrack.getTotalLength());
};

const _calculateFlowSpeed = (flow) => {
  return (Math.min(FLOW_DURATION_MAX_SECS, FLOW_DURATION_MIN_SECS + Math.max(0, FLOW_POWER_MAX_KW - flow) * (FLOW_DURATION_MAX_SECS - FLOW_DURATION_MIN_SECS) / FLOW_POWER_MAX_KW)) / _getTrackMaxLength();
};

const _polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
  const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
  return {
    x: centerX + (radius * Math.cos(angleInRadians)),
    y: centerY + (radius * Math.sin(angleInRadians))
  };
};

const _describeArc = (x, y, radius, startAngle, endAngle) => {
  const start = _polarToCartesian(x, y, radius, endAngle);
  const end = _polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y].join(' ');
};

export const init = (svgContainer) => {
  if (!svgContainer) {
    console.error('powerFlow: SVG container not found');
    return;
  }

  _svgContainer = svgContainer;
  _svg = svgContainer.querySelector('svg');
  if (!_svg) {
    console.error('powerFlow: SVG element not found');
    return;
  }

  _flowPv = _svg.getElementById('flowPv');
  _flowConsumer = _svg.getElementById('flowConsumer');
  _flowGrid = _svg.getElementById('flowGrid');
  if (!_flowPv || !_flowConsumer || !_flowGrid) {
    console.error('powerFlow: One or more flow elements not found', {_flowPv, _flowConsumer, _flowGrid});
    return;
  }

  _circleConsumerPv = _flowConsumer.querySelector('.flow-circle.consumer-pv');
  _circleConsumerGrid = _flowConsumer.querySelector('.flow-circle.consumer-grid');

  _circleConsumerPv.setAttribute('d', _describeArc(0, 0, 40, 0, 0));
  _circleConsumerGrid.setAttribute('d', _describeArc(0, 0, 40, 0, 360));

  _pvToConsumerTrack = _svg.querySelector('.pvToConsumerTrack');
  _pvToGridTrack = _svg.querySelector('.pvToGridTrack');
  _gridToConsumerTrack = _svg.querySelector('.gridToConsumerTrack');

  _pvToConsumerDot = _svg.querySelector('.pvToConsumerDot');
  _pvToGridDot = _svg.querySelector('.pvToGridDot');
  _gridToConsumerDot = _svg.querySelector('.gridToConsumerDot');

  _pvPower = _svg.getElementById('pvPower');
  _gridPower = _svg.getElementById('gridPower');
  _consumerPower = _svg.getElementById('consumerPower');
  _pvToConsumerPower = _svg.getElementById('pvToConsumerPower');
  _pvToGridPower = _svg.getElementById('pvToGridPower');
  _gridToConsumerPower = _svg.getElementById('gridToConsumerPower');

  _pvToConsumerFlow = 0;
  _pvToConsumerDirty = true;
  _pvToGridFlow = 0;
  _pvToGridDirty = true;
  _gridToConsumerFlow = 0;
  _gridToConsumerDirty = true;

  _resizeObserver.observe(_svgContainer);

  _pvToConsumerDot.addEventListener('animationiteration', () => {
    if (!_pvToConsumerDirty) { return; }
    _pvToConsumerDot.style.animation = 'none';
    requestAnimationFrame(() => {
      setTimeout(() => {
        let duration = 1;
        if (_pvToConsumerFlow <= 0.05) {
          _pvToConsumerDot.style.opacity = 0;
        } else {
          _pvToConsumerDot.style.opacity = 1;
          duration = _calculateFlowSpeed(_pvToConsumerFlow) * _pvToConsumerTrack.getTotalLength();
        }
        _pvToConsumerDot.style.animation = `pvToConsumerMove ${duration}s linear infinite`;
        _pvToConsumerDirty = false;
      }, 100);
    });
  });

  _pvToGridDot.addEventListener('animationiteration', () => {
    if (!_pvToGridDirty) { return; }
    _pvToGridDot.style.animation = 'none';
    requestAnimationFrame(() => {
      setTimeout(() => {
        let duration = 1;
        if (_pvToGridFlow <= 0.05) {
          _pvToGridDot.style.opacity = 0;
        } else {
          _pvToGridDot.style.opacity = 1;
          duration = _calculateFlowSpeed(_pvToGridFlow) * _pvToGridTrack.getTotalLength();
        }
        _pvToGridDot.style.animation = `pvToGridDotMove ${duration}s linear infinite`;
        _pvToGridDirty = false;
      }, 100);
    });
  });

  _gridToConsumerDot.addEventListener('animationiteration', () => {
    if (!_gridToConsumerDirty) { return; }
    _gridToConsumerDot.style.animation = 'none';
    requestAnimationFrame(() => {
      setTimeout(() => {
        let duration = 1;
        if (_gridToConsumerFlow <= 0.05) {
          _gridToConsumerDot.style.opacity = 0;
        } else {
          _gridToConsumerDot.style.opacity = 1;
          duration = _calculateFlowSpeed(_gridToConsumerFlow) * _gridToConsumerTrack.getTotalLength();
        }
        _gridToConsumerDot.style.animation = `gridToConsumerMove ${duration}s linear infinite`;
        _gridToConsumerDirty = false;
      }, 100);
    });
  });
};

const updateFlows = (pvToConsumerFlow, pvToGridFlow, gridToConsumerFlow) => {
  _pvToConsumerFlow = pvToConsumerFlow;
  _pvToConsumerDirty = true;
  _pvToConsumerPower.textContent = pvToConsumerFlow.toFixed(1) + ' kW';

  _pvToGridFlow = pvToGridFlow;
  _pvToGridDirty = true;
  _pvToGridPower.textContent = pvToGridFlow.toFixed(1) + ' kW';

  _gridToConsumerFlow = gridToConsumerFlow;
  _gridToConsumerDirty = true;
  _gridToConsumerPower.textContent = gridToConsumerFlow.toFixed(1) + ' kW';

  _pvPower.textContent = (pvToConsumerFlow + pvToGridFlow).toFixed(1) + ' kW';

  // Negativ = Einspeisung ins Netz, Positiv = Bezug aus dem Netz.
  _gridPower.textContent = (gridToConsumerFlow - pvToGridFlow).toFixed(1) + ' kW';

  _consumerPower.textContent = (pvToConsumerFlow + gridToConsumerFlow).toFixed(1) + ' kW';

  let p = 0;
  const t = pvToConsumerFlow + gridToConsumerFlow;
  if (t > 0) {
    p = 360 * pvToConsumerFlow / t;
  }

  _circleConsumerPv.setAttribute('d', _describeArc(0, 0, 40, 0, p));
  _circleConsumerGrid.setAttribute('d', _describeArc(0, 0, 40, p, 360));
};

document.addEventListener('latestDataUpdate', (e) => {
  updateFlows(e.detail.pvToConsumer, e.detail.pvToGrid, e.detail.gridToConsumer);
});
